import { SingleCheckOptionItem, SingleOptionField } from '@connected/form-fields/components/option';

import type { FormTableFieldApi } from '../types';

export type BooleanSelectFieldProps = {
    /** Field API from FormTable cell renderer */
    field: FormTableFieldApi<boolean | null | undefined>;
    /** Label for true option */
    trueLabel?: string;
    /** Label for false option */
    falseLabel?: string;
    /** Whether the field is disabled */
    disabled?: boolean;
    /** Additional class name */
    className?: string;
    /** Called when field value changes (for custom side effects like API patching) */
    onValueChange?: (value: boolean | null) => void;
};

/**
 * BooleanSelectField - A boolean dropdown select wrapper for use in FormTable cells.
 *
 * Integrates with FormTable's field API to provide form-connected boolean select input.
 * Displays two options: true and false with customizable labels.
 */
export function BooleanSelectField({
    field,
    trueLabel = 'Yes',
    falseLabel = 'No',
    disabled = false,
    className = 'w-full',
    onValueChange,
}: BooleanSelectFieldProps) {
    const options = [
        { label: trueLabel, value: true },
        { label: falseLabel, value: false },
    ];

    return (
        <SingleOptionField
            name={field.name}
            state={{ value: field.state.value as boolean }}
            handleChange={(value) => {
                field.handleChange(value);
                // Trigger side effect (e.g., API patch) immediately on change
                if (onValueChange) {
                    onValueChange(value as boolean | null);
                }
            }}
            options={options}
            render={SingleCheckOptionItem}
            dropdown
            closeOnClick
            disabled={disabled}
            className={className}
        />
    );
}
